import React, { useState } from 'react';
import {
  alpha,
  Box,
  Button,
  darken,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Close, Whatshot } from '@mui/icons-material';
import { getCellColor } from '../utils/cellColors';
import { DriverStreak, StreakRace } from '../services/api';

const lastName = (driver: string) =>
  driver
    .replace(/,? Jr\.?$/, '')
    .split(' ')
    .pop();

/** "T5 · 7/10" (amber, fire icon): the driver's top 5s (or top 10s) in their last races at this track and the
 * tracks most like it. The tooltip gives the detail; clicking opens the races in a dialog. */
export const StreakPill: React.FC<{
  streak: DriverStreak;
  site: string;
  tracks: string[];
  since: number;
  compact?: boolean;
}> = ({ streak, site, tracks, since, compact }) => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const color = theme.palette.warning.main;
  const label = `${streak.kind === 'top5' ? 'T5' : 'T10'} · ${streak.count}/${streak.of}`;
  const tip = `${streak.count} ${streak.kind === 'top5' ? 'top 5s' : 'top 10s'} in ${lastName(streak.driver)}'s last ${streak.of} races at ${site} and similar tracks. Click for the races.`;
  return (
    <>
      <Tooltip title={tip} enterTouchDelay={0}>
        <Box
          component="span"
          role="button"
          tabIndex={0}
          aria-label={tip}
          onClick={(e) => {
            e.stopPropagation();
            setOpen(true);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.stopPropagation();
              setOpen(true);
            }
          }}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.4,
            px: 0.75,
            py: compact ? 0 : 0.1,
            borderRadius: 999,
            bgcolor: alpha(color, theme.palette.mode === 'dark' ? 0.16 : 0.14),
            color: theme.palette.mode === 'dark' ? color : darken(color, 0.3),
            fontSize: compact ? 10 : 10.5,
            fontWeight: 600,
            lineHeight: 1.5,
            whiteSpace: 'nowrap',
            cursor: 'pointer',
            '&:hover': { bgcolor: alpha(color, 0.28) },
          }}
        >
          <Whatshot sx={{ fontSize: compact ? 12 : 13 }} />
          {label}
        </Box>
      </Tooltip>
      {open && (
        <StreakDialog
          streak={streak}
          site={site}
          tracks={tracks}
          since={since}
          open={open}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
};

const fmtRaceDate = (d: string) =>
  new Date(`${d}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

/** The races behind a streak pill: the driver's last races here and at similar tracks, newest first. */
const StreakDialog: React.FC<{
  streak: DriverStreak;
  site: string;
  tracks: string[];
  since: number;
  open: boolean;
  onClose: () => void;
}> = ({ streak, site, tracks, since, open, onClose }) => {
  const theme = useTheme();
  const phone = useMediaQuery(theme.breakpoints.down('sm'));
  const races = streak.races;
  const others = tracks.filter((t) => t !== site);
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={phone}
      fullWidth
      maxWidth="xs"
      onClick={(e) => e.stopPropagation()}
    >
      <DialogTitle sx={{ pr: 6 }}>
        {streak.driver}&apos;s last {streak.of} races
        <Typography variant="body2" color="text.secondary">
          At {site}
          {others.length > 0 ? ` and the tracks most like it (${others.join(', ')})` : ''}
        </Typography>
        <IconButton aria-label="Close" onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <Close />
        </IconButton>
      </DialogTitle>
      <Stack direction="row" spacing={1} sx={{ px: 3, pb: 1.5 }}>
        {[
          { label: 'Top 5s', v: streak.top5, on: streak.kind === 'top5' },
          { label: 'Top 10s', v: streak.top10, on: streak.kind === 'top10' },
          {
            label: 'Avg finish',
            v: (streak.races.reduce((a, r) => a + r.finish, 0) / streak.races.length).toFixed(1),
            on: false,
          },
        ].map((s) => (
          <Box
            key={s.label}
            sx={{
              flex: 1,
              p: 1,
              borderRadius: 2,
              border: 1,
              borderColor: s.on ? 'warning.main' : 'divider',
              bgcolor: s.on ? alpha(theme.palette.warning.main, 0.1) : undefined,
            }}
          >
            <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.6, color: 'text.secondary' }}>
              {s.label.toUpperCase()}
            </Typography>
            <Typography sx={{ fontSize: 20, fontWeight: 700 }}>
              {s.v}
              {s.label !== 'Avg finish' && (
                <Box component="span" sx={{ fontSize: 13, color: 'text.secondary', fontWeight: 500 }}>
                  {' '}
                  of {streak.of}
                </Box>
              )}
            </Typography>
          </Box>
        ))}
      </Stack>
      <DialogContent dividers>
        <StreakRaceList races={races} site={site} kind={streak.kind} />
        <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1.5 }}>
          Points races since {since}. {site} rows are highlighted; the fire marks a{' '}
          {streak.kind === 'top5' ? 'top-5' : 'top-10'} finish. Similar tracks are the ones the model finds most alike
          in how drivers finish there.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/** A driver's recent races here and at similar tracks, newest first: track, date, start and finish (the table's
 * colors), with this track's races highlighted and, for a streak, a fire on each top 5 (or top 10). Shared by the
 * streak dialog and the Simulator's driver panel. */
export const StreakRaceList: React.FC<{
  races: StreakRace[];
  site: string;
  kind?: DriverStreak['kind'];
  /** Adds the average running position in each race (the Tiers sidebar). */
  showRunning?: boolean;
}> = ({ races, site, kind, showRunning }) => {
  const theme = useTheme();
  const heads = showRunning ? ['RACE', 'START', 'RUN', 'FINISH'] : ['RACE', 'START', 'FINISH'];
  return (
    <>
      <Stack direction="row" spacing={1.5} sx={{ px: 1, pb: 0.75 }}>
        {heads.map((h, i) => (
          <Typography
            key={h}
            title={h === 'RUN' ? 'Average running position' : undefined}
            sx={{
              flex: i === 0 ? 1 : undefined,
              width: i === 0 ? undefined : h === 'FINISH' ? 64 : 44,
              textAlign: i === 0 ? 'left' : 'right',
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: 0.6,
              color: 'text.secondary',
            }}
          >
            {h}
          </Typography>
        ))}
      </Stack>
      {races.map((r, i) => {
        const me = r.site === site;
        const hit = kind === 'top5' ? r.finish <= 5 : kind === 'top10' ? r.finish <= 10 : false;
        return (
          <Stack
            key={r.date + r.site}
            direction="row"
            alignItems="center"
            spacing={1.5}
            sx={{
              py: 0.6,
              px: 1,
              borderRadius: 1,
              bgcolor: me ? alpha(theme.palette.primary.main, 0.12) : i % 2 ? 'action.hover' : undefined,
              boxShadow: me ? `inset 4px 0 0 ${theme.palette.primary.main}` : undefined,
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: 14, fontWeight: me ? 800 : 500 }} noWrap>
                {r.site}
              </Typography>
              <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>{fmtRaceDate(r.date)}</Typography>
            </Box>
            <Typography sx={{ width: 44, textAlign: 'right', fontSize: 13, color: 'text.secondary' }}>
              {r.start > 0 ? r.start : '–'}
            </Typography>
            {showRunning && (
              <Typography sx={{ width: 44, textAlign: 'right', fontSize: 13, color: 'text.secondary' }}>
                {r.running > 0 ? r.running.toFixed(1) : '–'}
              </Typography>
            )}
            <Box sx={{ width: 64, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 0.5 }}>
              {hit && <Whatshot sx={{ fontSize: 14, color: 'warning.main' }} />}
              <Box
                component="span"
                sx={{
                  width: 40,
                  textAlign: 'center',
                  py: 0.15,
                  borderRadius: 1,
                  bgcolor: getCellColor(r.finish, 'position'),
                  color: '#1b1b1b',
                  fontSize: 13.5,
                  fontWeight: 800,
                }}
              >
                {r.finish}
              </Box>
            </Box>
          </Stack>
        );
      })}
    </>
  );
};
